import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OsScroll from '../ui/OsScroll';
import { deviceScopeId } from '../../records/itemLocation';
import { listRecords } from '../../records/recordsClient';
import { isLimitedPlan } from '../../services/personalSyncConsent';
import { documentsStackedSections } from '../../utils/featureFlags';
import { preloadLexicalEditor } from '../ui/LexicalRichEditorLazy';
import { useDocumentsUiStore } from '../../extensions/bundled/documents/documentsUiStore';
import { useTabsStore } from '../../store/tabsStore';
import { useAuthStore } from '../../store/authStore';
import { useTeamsStore } from '../../store/teamsStore';
import SidebarHeader from './SidebarHeader';
import SidebarSearch from './SidebarSearch';
import SidebarFooter from './SidebarFooter';
import { useDocsLayoutStore } from '../document-editor/docsLayoutStore';
import TreeSection from './TreeSection';
import { scopeChoices, resolveChoice } from './scopeChoices';
import useWorkspaceControls from './useWorkspaceControls';
import WorkspaceDialogs from './WorkspaceDialogs';
import useSpaceHeader from './useSpaceHeader';
import SpaceDialogs from './SpaceDialogs';
import { openDocumentLinkText } from './openDocumentLink';
import './DocumentsSidebar.css';
import './sidebarChrome.css';

/** Whether "This device" is offered: Free plan, or any plan while this device still holds documents. */
function useDeviceSection(isAuthenticated, user) {
  const deviceScope = isAuthenticated ? deviceScopeId() : null;
  const [deviceDocCount, setDeviceDocCount] = useState(0);
  const treeVersion = useDocumentsUiStore((st) => st.treeVersion);
  useEffect(() => {
    if (!deviceScope) return undefined;
    let cancelled = false;
    listRecords({ workspaceId: deviceScope, extensionId: 'documents', recordType: null, scopeId: null })
      .then((rows) => {
        if (!cancelled) setDeviceDocCount(rows.filter((r) => r.record_type === 'document' || r.record_type === 'folder').length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [deviceScope, treeVersion]);
  return deviceScope != null && (isLimitedPlan(user) || deviceDocCount > 0);
}

/**
 * The Documents panel's sidebar (redesign, cards 1.2–1.8): the scope switcher
 * header, the search box, ONE tree — Pinned + All documents of the scope picked
 * in the header (remembered per device in documentsUiStore) — and the sync
 * footer. `documentsStackedSections()` brings back the old layout (every scope
 * as a stacked section) for one release. User workspaces (list 5, behind the
 * `documents.scopes` flag) are useWorkspaceControls + WorkspaceDialogs.
 */
export default function DocumentsSidebar({ enabled }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  // A tag chip above a document's title filters this box by the tag (card 2.3).
  const searchRequest = useDocsLayoutStore((s) => s.searchRequest);
  useEffect(() => {
    if (searchRequest) setSearchTerm(searchRequest.query);
  }, [searchRequest]);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const teams = useTeamsStore((s) => s.teams);
  const showDevice = useDeviceSection(isAuthenticated, user);
  const activeDocumentTabId = useTabsStore((s) => s.activeDocumentTabId);
  const treeVersion = useDocumentsUiStore((s) => s.treeVersion);
  const scopeKey = useDocumentsUiStore((s) => s.scopeKey);
  const setScopeKey = useDocumentsUiStore((s) => s.setScopeKey);
  const stacked = useMemo(() => documentsStackedSections(), []);
  const ws = useWorkspaceControls({ user });

  // One node across ALL trees is selected / renaming at a time — F2 and the
  // menus need to know which tree a selection belongs to, not just a node id.
  const [selected, setSelected] = useState(null); // { sectionId, id } | null
  const [editing, setEditing] = useState(null); // { sectionId, id } | null
  const searchRef = useRef(null);
  const treeRef = useRef(null);
  const createRef = useRef(null);
  const registerCreate = useCallback((ref) => {
    createRef.current = ref;
  }, []);

  // Warm the heavy Lexical chunk while the panel is open so opening a document
  // is an instant transition (no "Loading editor…" on first open).
  useEffect(() => {
    if (enabled) preloadLexicalEditor();
  }, [enabled]);

  const signedInTeams = useMemo(() => (isAuthenticated ? (teams || []).filter((tm) => tm?.id != null) : []), [isAuthenticated, teams]);
  const choices = useMemo(
    () =>
      scopeChoices({ t, showDevice, teams: signedInTeams, workspaces: ws.workspaces, workspacesOn: ws.workspacesOn }),
    [t, showDevice, signedInTeams, ws.workspaces, ws.workspacesOn]
  );
  const bucketChoice = resolveChoice(choices, scopeKey);
  // Under a project (card 4.3) the header offers Main + the project's
  // documentation spaces and the tree is the project's bucket, filtered.
  const spaces = useSpaceHeader({ t, bucketChoice });
  const current = spaces.inProject ? spaces.current : bucketChoice;
  const query = searchTerm.trim().toLowerCase();

  const shared = {
    query,
    treeVersion,
    activeDocumentTabId,
    selected,
    setSelected,
    editing,
    setEditing,
    workspaces: ws.workspaces,
  };

  const focusTree = () => {
    const root = treeRef.current;
    const row = root?.querySelector('[role="treeitem"][tabindex="0"]') || root?.querySelector('[role="treeitem"]');
    row?.focus();
  };

  const stackedSections = () =>
    choices.map((c) => (
      <TreeSection
        key={c.key}
        variant="stacked"
        sectionId={c.key}
        scope={c.scope}
        title={c.label}
        isTeam={c.kind === 'team'}
        eager={c.kind === 'personal'}
        listenTitlebarEvents={c.kind === 'personal'}
        emptyTitle={c.emptyTitle}
        emptyHint={c.emptyHint}
        {...shared}
      />
    ));

  return (
    <div className="doc-sidebar">
      {!stacked && (
        <SidebarHeader
          onNewDocument={() => createRef.current?.current?.newDocument()}
          onNewFolder={() => createRef.current?.current?.newFolder()}
          {...(spaces.inProject
            ? {
                choices: spaces.choices,
                current,
                onPick: (key) => {
                  setSelected(null);
                  spaces.onPick(key);
                },
                workspacesOn: true,
                onNewWorkspace: spaces.onNew,
                onManageWorkspaces: spaces.onManage,
                onMoveTree: spaces.onMoveTree,
                onReorderWorkspaces: spaces.onReorder,
                ns: 'docSidebar.spaces',
              }
            : {
                choices,
                current,
                onPick: (key) => {
                  setSelected(null);
                  setScopeKey(key);
                },
                userName: user?.name || user?.username || user?.email,
                workspacesOn: ws.workspacesOn,
                onNewWorkspace: () => ws.setDialog('new'),
                onManageWorkspaces: () => ws.setDialog('manage'),
                onMoveTree: ws.onMoveTree,
                onReorderWorkspaces: ws.onReorder,
              })}
        />
      )}
      <WorkspaceDialogs controls={ws} />
      <SpaceDialogs dialog={spaces.dialog} setDialog={spaces.setDialog} docSpaces={spaces.docSpaces} />
      <SidebarSearch
        value={searchTerm}
        onChange={setSearchTerm}
        onEscape={focusTree}
        onSubmit={(text) => {
          if (!openDocumentLinkText(text)) return false;
          setSearchTerm('');
          return true;
        }}
        inputRef={searchRef}
      />

      <OsScroll className="doc-sidebar-scroll flex-1 min-h-0">
        <div className="doc-sidebar-tree">
          {!enabled ? (
            <p className="doc-tree-note">{t('docSidebar.disabled')}</p>
          ) : stacked ? (
            stackedSections()
          ) : (
            <TreeSection
              key={current.sectionId || current.key}
              sectionId={current.sectionId || current.key}
              scope={current.scope}
              title={current.label}
              eager
              listenTitlebarEvents
              emptyTitle={current.emptyTitle}
              emptyHint={current.emptyHint}
              onRegisterCreate={registerCreate}
              treeRef={treeRef}
              {...shared}
            />
          )}
        </div>
      </OsScroll>

      {!stacked && enabled && <SidebarFooter scope={current.scope} signedIn={isAuthenticated} />}
    </div>
  );
}
